import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    gigs: Array,
    centerLat: Number,
    centerLng: Number,
    zoom: Number
  }

  connect() {
    // Vérifier que Mapbox est chargé
    if (typeof mapboxgl === 'undefined') {
      console.error("Mapbox GL JS n'est pas chargé")
      return
    }
    
    this.initializeMap()
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }

  initializeMap() {
    try {
      // Configurer le token d'accès
      mapboxgl.accessToken = this.authToken
      
      // Initialiser la carte Mapbox
      this.map = new mapboxgl.Map({
        container: this.element,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [this.centerLngValue || 2.3522, this.centerLatValue || 48.8566], // Paris par défaut
        zoom: this.zoomValue || 5,
        projection: 'mercator' // Carte plate (pas de globe)
      })

      // Désactiver le zoom au scroll pour permettre le scroll de la page
      this.map.scrollZoom.disable()

      this.map.addControl(new mapboxgl.NavigationControl())

      this.map.on('load', () => {
        this.addGigMarkers()
        if (this.gigsValue.length > 0) {
          this.fitMapToMarkers()
        }
      })
      
      this.map.on('error', (e) => {
        console.error("Mapbox error:", e)
      })
      
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la carte:", error)
    }
  }

  addGigMarkers() {
    if (!this.gigsValue || this.gigsValue.length === 0) {
      return
    }

    // Filtrer les gigs qui ont des coordonnées valides
    const gigsWithCoordinates = this.gigsValue.filter(gig =>
      gig.city && gig.city.trim() !== '' && gig.latitude && gig.longitude
    )

    if (gigsWithCoordinates.length === 0) {
      return
    }

    // Créer les markers pour chaque ville unique
    const cityGroups = this.groupGigsByCity(gigsWithCoordinates)
    const bounds = new mapboxgl.LngLatBounds()

    // Store coordinates in chronological order for route drawing
    const orderedCoordinates = []

    for (const [city, gigs] of Object.entries(cityGroups)) {
      // Utiliser les coordonnées du premier gig de cette ville (tous les gigs d'une même ville ont les mêmes coordonnées)
      const coordinates = [gigs[0].longitude, gigs[0].latitude]

      this.createMarker(coordinates, city, gigs)
      bounds.extend(coordinates)

      // Store with order_index for sorting
      const orderIndex = Math.min(...gigs.map(gig => gig.order_index))
      orderedCoordinates.push({ coordinates, orderIndex })
    }

    // Sort coordinates by chronological order
    orderedCoordinates.sort((a, b) => a.orderIndex - b.orderIndex)

    // Draw driving route between all points
    if (orderedCoordinates.length >= 2) {
      this.drawDrivingRoute(orderedCoordinates.map(c => c.coordinates))
    }

    // Ajuster la vue de la carte pour inclure tous les markers (sans animation)
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 10,
        animate: false
      })
    }
  }

  groupGigsByCity(gigs) {
    return gigs.reduce((groups, gig) => {
      const city = gig.city.trim()
      if (!groups[city]) {
        groups[city] = []
      }
      groups[city].push(gig)
      return groups
    }, {})
  }

  createMarker(coordinates, city, gigs) {
    // Créer le popup avec les informations des gigs
    const popupContent = this.createPopupContent(city, gigs)

    const popup = new mapboxgl.Popup({
      offset: 25
    }).setHTML(popupContent)

    // Use the order_index of the first (earliest) gig in this city
    // Gigs are already sorted by date, so the first one has the lowest order_index
    const orderIndex = Math.min(...gigs.map(gig => gig.order_index))

    // Créer l'élément marker personnalisé
    const markerEl = document.createElement('div')
    markerEl.className = 'touring-marker'
    markerEl.style.cssText = `
      background-color: #10b981;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 12px;
    `
    markerEl.textContent = orderIndex.toString()

    // Créer le marker
    new mapboxgl.Marker(markerEl)
      .setLngLat(coordinates)
      .setPopup(popup)
      .addTo(this.map)
  }

  createPopupContent(city, gigs) {
    const gigsList = gigs.map(gig => {
      const date = new Date(gig.date).toLocaleDateString('fr-FR')
      const time = gig.time ? ` - ${gig.time}` : ''
      const venue = gig.venue_name ? ` @ ${gig.venue_name}` : ''
      const description = gig.description ? ` (${gig.description})` : ''
      
      return `<li class="text-sm text-gray-700 mb-1">
        <strong>${date}${time}</strong>${venue}${description}
      </li>`
    }).join('')

    return `
      <div class="p-2">
        <h3 class="font-semibold text-lg text-gray-900 mb-2">${city}</h3>
        <p class="text-sm text-gray-600 mb-2">
          ${gigs.length} concert${gigs.length > 1 ? 's' : ''}
        </p>
        <ul class="list-none">
          ${gigsList}
        </ul>
      </div>
    `
  }

  fitMapToMarkers() {
    // Cette méthode sera appelée après l'ajout de tous les markers
    // La logique de fit est déjà gérée dans addGigMarkers()
  }

  async drawDrivingRoute(coordinates) {
    if (coordinates.length < 2) return

    try {
      // Mapbox Directions API accepts up to 25 waypoints
      // Format: lng,lat;lng,lat;...
      const waypointsString = coordinates
        .map(coord => `${coord[0]},${coord[1]}`)
        .join(';')

      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${waypointsString}?geometries=geojson&overview=full&access_token=${this.authToken}`
      )

      const data = await response.json()

      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0].geometry

        // Add the route as a layer on the map
        if (this.map.getSource('route')) {
          this.map.getSource('route').setData(route)
        } else {
          this.map.addSource('route', {
            type: 'geojson',
            data: route
          })

          this.map.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#10b981',
              'line-width': 4,
              'line-opacity': 0.7
            }
          })
        }
      }
    } catch (error) {
      console.error('Erreur lors du tracé de l\'itinéraire:', error)
    }
  }

  get authToken() {
    const meta = document.head.querySelector('meta[name="mapbox_token"]')
    if (!meta) {
      console.error("Token Mapbox non trouvé dans les meta tags")
      return null
    }
    return meta.content
  }
}